import { useEffect, useState } from "react";
import {
  FlaskConical,
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
  Factory,
  Boxes,
  Beaker,
  ShieldCheck,
  Gauge,
  Ruler,
  Timer,
  Waves,
  Sun,
  Eye,
  Bluetooth,
  Weight,
  Database,
  Download,
} from "lucide-react";
import CI62Gate from "@/components/ci62/CI62Gate";
import CI62DemoForm from "@/components/ci62/CI62DemoForm";
import CI62DownloadModal from "@/components/ci62/CI62DownloadModal";

const NAVY = "#0A1628";

const SPECS = [
  { icon: Waves, label: "Measurement Geometry", value: "d/8° sphere (SPIN / SPEX)" },
  { icon: Gauge, label: "Spectral Range", value: "400–700 nm · 10 nm interval" },
  { icon: CheckCircle2, label: "Inter-Instrument Agreement", value: "0.20 ΔE*ab avg." },
  { icon: CheckCircle2, label: "Repeatability", value: "0.05 ΔE*ab (white ceramic)" },
  { icon: Timer, label: "Measurement Time", value: "≈ 2 seconds" },
  { icon: Ruler, label: "Apertures", value: "14 mm · 8 mm · 4 mm" },
  { icon: Sun, label: "Illuminants", value: "A, C, D50, D65, F2, F7, F11, F12" },
  { icon: Eye, label: "Standard Observers", value: "2° & 10°" },
  { icon: Bluetooth, label: "Connectivity", value: "USB 2.0 & Bluetooth" },
  { icon: Weight, label: "Weight", value: "1.06 kg — fully portable" },
  { icon: Database, label: "Onboard Storage", value: "4,000 samples · 1,000 standards" },
  { icon: ShieldCheck, label: "Whiteness / Yellowness Indices", value: "WI & YI (multiple standards)" },
];

const USE_CASES = [
  {
    icon: Factory,
    title: "API Manufacturer",
    body: "In-process and batch-release color verification of active pharmaceutical ingredients — objective ΔE data replaces subjective visual checks at every critical control point.",
  },
  {
    icon: Boxes,
    title: "Excipient Supplier",
    body: "Incoming QC color conformance against an approved reference standard — accept or reject each lot on numeric tolerances, documented and defensible.",
  },
  {
    icon: Beaker,
    title: "Formulation Team",
    body: "Color matching during development and release testing of finished product — ensure batch-to-batch consistency that reflects formulation integrity.",
  },
];

export default function CI62() {
  const [unlocked, setUnlocked] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("ci62_unlocked") === "1") setUnlocked(true);
    document.title = "Color Quality Control | Xpertee Pharma Services";
  }, []);

  if (!unlocked) return <CI62Gate onUnlock={() => setUnlocked(true)} />;

  const triggerPrint = () => window.print();

  const handleDownload = () => {
    if (sessionStorage.getItem("ci62_lead_captured") === "1") {
      triggerPrint();
    } else {
      setDownloadModalOpen(true);
    }
  };

  return (
    <div data-testid="ci62-page" className="ci62-print-root min-h-screen bg-white text-slate-900 font-sans">
      <CI62DownloadModal
        open={downloadModalOpen}
        onOpenChange={setDownloadModalOpen}
        onCaptured={() => setTimeout(triggerPrint, 350)}
      />
      {/* Top bar */}
      <header className="no-print sticky top-0 z-40 bg-[#0A1628] border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img
              src="/logo.png"
              alt="Xpertee"
              className="h-10 w-auto"
              width="175"
              height="40"
            />
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleDownload}
              data-testid="ci62-download-btn"
              className="inline-flex items-center gap-2 border border-white/25 text-white hover:bg-white/10 px-4 h-10 text-sm font-medium transition-colors"
            >
              <Download className="w-4 h-4" strokeWidth={1.75} />
              <span className="hidden sm:inline">Download Info</span>
            </button>
            <a
              href="#demo"
              data-testid="ci62-topbar-cta"
              className="hidden sm:inline-flex items-center bg-teal-600 hover:bg-teal-500 text-white px-5 h-10 text-sm font-medium transition-colors"
            >
              Request a Demo
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section
        data-testid="ci62-hero"
        className="relative text-white overflow-hidden"
        style={{ backgroundColor: NAVY }}
      >
        <div className="absolute inset-0 bg-lab-grid opacity-[0.06]" aria-hidden />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-teal-400 font-medium mb-6">
              <span className="w-8 h-px bg-teal-400" />
              X-Rite CI62 · Pharmaceutical Color QC
            </div>
            <h1 className="font-pub text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight">
              Color is a Critical Quality Attribute. <span className="text-teal-400">Measure It.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-slate-300 leading-relaxed max-w-2xl">
              In pharmaceutical powders, color consistency directly reflects API potency, purity, and formulation
              integrity.
            </p>
            <div className="mt-10 no-print flex flex-col sm:flex-row gap-4">
              <a
                href="#demo"
                data-testid="ci62-hero-cta"
                className="group inline-flex items-center justify-center bg-teal-600 hover:bg-teal-500 text-white px-8 h-14 text-sm font-medium tracking-wide transition-colors"
              >
                Request a Demo
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
              </a>
              <button
                onClick={handleDownload}
                data-testid="ci62-hero-download"
                className="inline-flex items-center justify-center border border-white/30 text-white hover:bg-white/10 px-8 h-14 text-sm font-medium tracking-wide transition-colors"
              >
                <Download className="mr-2 w-4 h-4" strokeWidth={1.75} />
                Download Info (PDF)
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section data-testid="ci62-problem" className="bg-white py-20 lg:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 text-teal-700 mb-5">
                <AlertTriangle className="w-5 h-5" strokeWidth={1.75} />
                <span className="text-xs uppercase tracking-[0.2em] font-medium">The Risk of Subjectivity</span>
              </div>
              <h2 className="font-pub text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 leading-tight">
                Visual inspection is subjective. Your batch records shouldn&rsquo;t be.
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-5 text-slate-600 text-lg leading-relaxed">
              <p>
                During manufacturing and at the time of batch release, visual color inspection is subjective and
                unreliable. Two analysts, two lighting conditions, two verdicts.
              </p>
              <p>
                A shift in powder color — even a subtle one — can indicate{" "}
                <strong className="text-slate-900 font-semibold">increased impurities</strong>,{" "}
                <strong className="text-slate-900 font-semibold">particle size distribution outside specification</strong>,
                or <strong className="text-slate-900 font-semibold">raw material variability</strong>. These deviations
                are costly to miss and difficult to defend in an audit.
              </p>
              <p>
                The CI62 replaces opinion with data: every measurement is a numeric, traceable ΔE value tied to an
                approved standard — the kind of evidence QC and QA teams can stand behind.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* In-process + release application */}
      <section data-testid="ci62-application" className="py-20 lg:py-28" style={{ backgroundColor: "#F1F5F9" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-teal-700 font-medium mb-4">
              A Process Control Tool — Not Just a Lab Instrument
            </div>
            <h2 className="font-pub text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 leading-tight">
              One instrument. Two quality gates.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 p-8">
              <div className="font-pub text-teal-700 text-sm font-semibold uppercase tracking-wider">In-Process</div>
              <h3 className="mt-3 font-pub text-2xl font-semibold text-slate-900">During Manufacturing</h3>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Verify color at intermediate stages — blending, drying, milling — to catch deviations before they
                propagate downstream. Correct the process while correction is still possible.
              </p>
            </div>
            <div className="bg-white border border-slate-200 p-8">
              <div className="font-pub text-teal-700 text-sm font-semibold uppercase tracking-wider">Release</div>
              <h3 className="mt-3 font-pub text-2xl font-semibold text-slate-900">At Batch Release</h3>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Confirm the finished batch conforms to the approved color standard before it ships — a documented,
                objective release criterion that stands up to inspection.
              </p>
            </div>
          </div>
          <div className="mt-8 border-l-2 border-teal-600 bg-white px-8 py-6">
            <p className="text-slate-700 text-lg leading-relaxed">
              Color measurement at both stages creates a <strong>documented quality gate</strong> — aligned with{" "}
              <strong>Quality by Design (QbD)</strong> principles. This is not just a lab instrument; it is a process
              control tool.
            </p>
          </div>
        </div>
      </section>

      {/* Hero visual */}
      <section data-testid="ci62-visual" className="py-20 lg:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <figure className="relative">
              <img
                src="https://images.unsplash.com/photo-1748349221526-33b51820b21e"
                alt="Pharmaceutical analyst comparing an off-white powder sample against a white reference standard and flagging the deviation"
                className="w-full h-[440px] object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-slate-900/10" aria-hidden />
              <figcaption className="mt-5 flex items-center gap-3 text-slate-500 text-sm italic font-pub">
                <span className="w-8 h-px bg-teal-600" />
                &ldquo;What the eye misses, the CI62 catches.&rdquo;
              </figcaption>
            </figure>
            <div>
              <h2 className="font-pub text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 leading-tight">
                The deviation the eye approves, the data rejects.
              </h2>
              <p className="mt-6 text-slate-600 text-lg leading-relaxed">
                A powder that looks &ldquo;close enough&rdquo; against a white reference can still fall outside
                specification. Where a trained eye hesitates, the CI62 delivers a definitive numeric verdict — enabling
                the analyst to confidently flag the deviation and reject the batch.
              </p>
              <ul className="mt-8 space-y-3">
                {[
                  "Objective ΔE value against an approved standard",
                  "Repeatable across analysts, shifts and sites",
                  "Instant pass / fail verdict at the point of inspection",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-teal-600 mt-0.5 shrink-0" strokeWidth={1.75} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Product highlight */}
      <section data-testid="ci62-product" className="py-20 lg:py-28" style={{ backgroundColor: NAVY }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.2em] text-teal-400 font-medium mb-4">The Instrument</div>
              <h2 className="font-pub text-3xl sm:text-4xl font-semibold tracking-tight text-white leading-tight">
                X-Rite CI62 Handheld Spectrophotometer
              </h2>
              <p className="mt-6 text-slate-300 text-lg leading-relaxed">
                A portable, non-contact, non-destructive, laboratory-grade sphere spectrophotometer engineered for
                precise, repeatable color measurement of pharmaceutical powders and solids.
              </p>
              <div className="mt-8 bg-white p-6 flex items-center justify-center">
                <img
                  src="https://www.xrite.com/-/media/global-product-images/c/ci6x-family-product-redesign/ci62/ci62-spectrophotometer-01-700x350.png"
                  alt="X-Rite CI62 handheld spectrophotometer"
                  className="max-h-56 w-auto object-contain"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
                {SPECS.map((s) => {
                  const Icon = s.icon;
                  return (
                    <div key={s.label} className="bg-[#0A1628] p-5">
                      <Icon className="w-5 h-5 text-teal-400" strokeWidth={1.5} />
                      <div className="mt-3 text-[11px] uppercase tracking-[0.14em] text-slate-400 font-medium">
                        {s.label}
                      </div>
                      <div className="mt-1 text-white font-medium text-sm leading-snug">{s.value}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section data-testid="ci62-usecases" className="py-20 lg:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-teal-700 font-medium mb-4">Where It Delivers</div>
            <h2 className="font-pub text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 leading-tight">
              Built into the pharmaceutical supply chain.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {USE_CASES.map((u) => {
              const Icon = u.icon;
              return (
                <div
                  key={u.title}
                  data-testid={`ci62-usecase-${u.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="border border-slate-200 p-8 hover:border-teal-500 transition-colors"
                >
                  <span className="inline-flex items-center justify-center w-12 h-12 bg-[#0A1628] text-teal-400">
                    <Icon className="w-6 h-6" strokeWidth={1.5} />
                  </span>
                  <h3 className="mt-6 font-pub text-xl font-semibold text-slate-900">{u.title}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed text-sm">{u.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why it matters */}
      <section data-testid="ci62-why" className="py-20 lg:py-28" style={{ backgroundColor: "#F1F5F9" }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-teal-700 font-medium mb-5">Why It Matters</div>
          <h2 className="font-pub text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900 leading-tight">
            Quality by Design, in practice.
          </h2>
          <p className="mt-8 text-slate-600 text-lg lg:text-xl leading-relaxed">
            Color data captured at every stage supports <strong className="text-slate-900">cGMP documentation</strong>,{" "}
            <strong className="text-slate-900">batch records</strong>, and{" "}
            <strong className="text-slate-900">audit readiness</strong>. When an inspector asks how you know a batch
            conformed, you answer with numbers — not judgement.
          </p>
        </div>
      </section>

      {/* Demo form */}
      <section id="demo" data-testid="ci62-demo" className="no-print py-20 lg:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-teal-700 font-medium mb-4">Get Started</div>
              <h2 className="font-pub text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 leading-tight">
                Request a Demo
              </h2>
              <p className="mt-6 text-slate-600 text-lg leading-relaxed">
                See how the CI62 fits into your in-process and release testing workflow. Our specialists will walk you
                through sample measurement, tolerance setup and documentation for your specific application.
              </p>
              <div className="mt-8 border-l-2 border-teal-600 pl-5 text-slate-600">
                <p className="font-pub italic">
                  Objective color control, delivered by pharmaceutical quality experts.
                </p>
              </div>
            </div>
            <div className="bg-slate-50 border border-slate-200 p-8">
              <CI62DemoForm />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A1628] text-slate-400 py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm">© {new Date().getFullYear()} Xpertee Pharma Services Private Limited</p>
          <p className="text-xs text-slate-500">
            CI62 is a product of X-Rite. Xpertee Pharma Services is an independent solutions partner.
          </p>
        </div>
      </footer>
    </div>
  );
}
